import { useState, useEffect } from "react";
import { MapPin, Loader2, Crosshair, Star, X, Globe } from "lucide-react";
import { STATUS_LABELS } from "@/lib/format";
import GooglePlaceSearch from "@/components/GooglePlaceSearch";

const STATUS_OPTIONS = ["not_visited", "not_interested"];

export default function AddGarageForm({ onSubmit, busy, big }) {
  const [form, setForm] = useState({
    name: "",
    area: "",
    status: "not_visited",
    phone: "",
    buyer_name: "",
    notes: "",
    latitude: null,
    longitude: null,
  });
  const [geoStatus, setGeoStatus] = useState("");
  const [gplace, setGplace] = useState(null);

  const grabLocation = () => {
    setGeoStatus("locating");
    if (!navigator.geolocation) {
      setGeoStatus("error");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        setGeoStatus("done");
      },
      () => setGeoStatus("error"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  useEffect(() => {
    // auto-grab GPS on mount for field use
    grabLocation();
    // eslint-disable-next-line
  }, []);

  const onPlace = (p) => {
    setGplace(p);
    // Google (New) UAE addresses use ' - ' separators: "Street - District - City - Country"
    let area = "Sharjah";
    if (p.address) {
      const parts = p.address.split(" - ").map((s) => s.trim()).filter(Boolean);
      if (parts.length >= 3) area = parts[parts.length - 3];
      else if (parts.length === 2) area = parts[0];
    }
    setForm((f) => ({
      ...f,
      name: p.name || f.name,
      area,
      phone: p.phone || f.phone,
      latitude: p.latitude ?? f.latitude,
      longitude: p.longitude ?? f.longitude,
    }));
    setGeoStatus("done");
  };

  const clearPlace = () => setGplace(null);

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form, source: gplace ? "google_search" : "manual" };
    if (gplace) {
      payload.google_place_id = gplace.google_place_id;
      payload.google_rating = gplace.google_rating;
      payload.google_review_count = gplace.google_review_count;
      payload.address = gplace.address;
    }
    onSubmit(payload);
  };

  const inputCls = `w-full ${big ? "h-14 text-base" : "h-11"} px-3 rounded-md border border-[#D1D5DB] bg-white text-[#111827] focus:outline-none focus:border-[#1E3A8A] focus:ring-1 focus:ring-[#1E3A8A]`;
  const labelCls = "block text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-1";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="add-garage-form">
      <div>
        <label className={labelCls}>Find on Google</label>
        <GooglePlaceSearch onSelect={onPlace} big={big} placeholder="Type a garage name…" />
        {gplace && (
          <div className="mt-3 border-2 border-[#1E3A8A]/30 rounded-lg p-3 bg-[#F9FAFB]" data-testid="google-profile-preview">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-bold text-[#111827]">{gplace.name}</div>
                <div className="text-xs text-[#6B7280]">{gplace.address}</div>
              </div>
              <button type="button" onClick={clearPlace} className="p-1 text-[#9CA3AF] hover:text-[#B91C1C]"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#374151]">
              {gplace.google_rating != null && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B]" /> {gplace.google_rating} ({gplace.google_review_count || 0})</span>}
              {gplace.phone && <span>{gplace.phone}</span>}
              {gplace.latitude && <span className="flex items-center gap-1 text-[#047857]"><MapPin className="w-3 h-3" /> Location captured</span>}
              {gplace.google_maps_uri && <a href={gplace.google_maps_uri} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#1E3A8A] font-medium"><Globe className="w-3 h-3" /> View profile</a>}
            </div>
            {gplace.opening_hours && gplace.opening_hours.length > 0 && (
              <div className="mt-2 text-[11px] text-[#6B7280] leading-relaxed">{gplace.opening_hours.join(" · ")}</div>
            )}
          </div>
        )}
        <div className="text-xs text-[#9CA3AF] mt-1.5">Pick a result to auto-fill everything, or enter details by hand below.</div>
      </div>

      <div>
        <label className={labelCls}>Garage name *</label>
        <input
          className={inputCls}
          required
          value={form.name}
          data-testid="add-garage-name"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Al Wahda Auto Repair"
        />
      </div>
      <div>
        <label className={labelCls}>Area *</label>
        <input
          className={inputCls}
          required
          value={form.area}
          data-testid="add-garage-area"
          onChange={(e) => setForm({ ...form, area: e.target.value })}
          placeholder="e.g. Industrial Area 5"
        />
      </div>
      <div>
        <label className={labelCls}>Status</label>
        <select
          className={inputCls}
          value={form.status}
          data-testid="add-garage-status"
          onChange={(e) => setForm({ ...form, status: e.target.value })}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Phone (optional)</label>
        <input
          className={inputCls}
          value={form.phone}
          data-testid="add-garage-phone"
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="05x xxx xxxx"
          inputMode="tel"
        />
      </div>
      <div>
        <label className={labelCls}>Buyer name (optional)</label>
        <input
          className={inputCls}
          value={form.buyer_name}
          data-testid="add-garage-buyer"
          onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
        />
      </div>
      <div>
        <label className={labelCls}>Note (optional)</label>
        <textarea
          className={`${inputCls.replace(big ? "h-14" : "h-11", "min-h-[64px] py-2")}`}
          value={form.notes}
          data-testid="add-garage-notes"
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>

      <button
        type="button"
        onClick={grabLocation}
        data-testid="add-garage-location-btn"
        className={`w-full ${big ? "h-14" : "h-11"} rounded-md border border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-center gap-2 font-medium text-[#111827] active:scale-[0.98] transition-transform`}
      >
        {geoStatus === "locating" ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Crosshair className="w-5 h-5 text-[#1E3A8A]" strokeWidth={2.5} />
        )}
        {form.latitude
          ? `Location captured ✓`
          : geoStatus === "error"
          ? "Location unavailable — tap to retry"
          : "Use my current location"}
      </button>

      <button
        type="submit"
        disabled={busy}
        data-testid="add-garage-submit"
        className={`w-full ${big ? "h-14 text-lg" : "h-12"} rounded-md bg-[#1E3A8A] text-white font-bold tracking-wide hover:bg-[#375DBA] active:scale-[0.98] transition-colors disabled:opacity-60 flex items-center justify-center gap-2`}
      >
        {busy && <Loader2 className="w-5 h-5 animate-spin" />}
        <MapPin className="w-5 h-5" strokeWidth={2.5} />
        Save garage
      </button>
    </form>
  );
}
